// src/utils/calculateTeamPoints.js

import { getLeagueConfig } from "../utils/leagueConfig";
import { LEAGUE_RULESETS } from "../utils/scoringRules";

/**
 * Calculate total fantasy points for a team in a given gameweek.
 *
 * Robust to different shapes we've had over time:
 *  - selections can be flat (DEF/MID/FWD) or nested under GW keys
 *  - captain/captains can be a string or a map of { GWx: id }
 *  - chipUsed/chipsUsed can be a string or a map of { GWx: name }
 *  - transfers can be saved under numeric keys, "GWx" keys, or not present
 *
 * @param {Object} team - Team doc (preferably the GW snapshot), may contain maxFreeTransfers
 * @param {Object} playerPoints - { [playerId]: { total, breakdown } }
 * @param {string} leagueTypeParam - Fallback league type if not on team
 * @param {string|number} currentGWParam - GW id (e.g. "GW2") or number (e.g. 2)
 * @returns {{ total:number, breakdowns:Object, selections:Object, captain?:string, chipUsed?:string, transferPenalty:number }}
 */
export function calculateTeamPoints(team, playerPoints, currentGWParam, leagueTypeParam) {
  if (!team) {
    console.warn("calculateTeamPoints: no team provided");
    return { total: 0, breakdowns: {}, selections: {}, transferPenalty: 0 };
  }

  // ---------- helpers ----------
  const normalizeLeagueType = (type) =>
    (type || "7aside").toLowerCase().replace(/[^a-z0-9]/g, "");

  const toGwKey = (gw) => {
    if (gw == null) return "GW1"; // sensible default
    if (typeof gw === "string") return gw.startsWith("GW") ? gw : `GW${gw}`;
    // number
    return `GW${gw}`;
  };

  // ---------- league + config ----------
  const leagueTypeRaw = team.leagueType || leagueTypeParam || "7aside";
  const leagueType = normalizeLeagueType(leagueTypeRaw);
  const leagueConfig = getLeagueConfig(leagueType) || {};
  const leaguePositions = Array.isArray(leagueConfig.positions)
    ? leagueConfig.positions
    : ["DEF", "MID", "FWD"]; // hard fallback

  // ---------- figure out GW + resolve shape differences ----------
  const currentGW = currentGWParam ?? team.currentGW ?? 1;
  const gwKey = toGwKey(currentGW);
  console.log("🔎 calculateTeamPoints GW debug", {
    currentGWParam,
    teamCurrentGW: team.currentGW,
    derivedCurrentGW: currentGW,
    gwKey,
  });

  // selections: either flat, or nested under GW key
  let selections = team.selections || {};
  const looksFlat = leaguePositions.every((p) => Array.isArray(selections[p]));
  if (!looksFlat && selections[gwKey]) {
    selections = selections[gwKey] || {};
  }

  // captain/captains: accept string or map
  let captain = team.captain ?? team.captains ?? null;
  if (captain && typeof captain === "object") captain = captain[gwKey] ?? null;

  // chipUsed/chipsUsed: accept string or map; normalize "None" to null
  let chipUsed = team.chipUsed ?? team.chipsUsed ?? null;
  if (chipUsed && typeof chipUsed === "object") chipUsed = chipUsed[gwKey] ?? null;
  if (chipUsed === "None") chipUsed = null;

  // ---------- build selected player ids ----------
  const selectedPlayers = leaguePositions
    .flatMap((pos) => (Array.isArray(selections[pos]) ? selections[pos] : []))
    .filter(Boolean);

  // ---------- chips/captain multiplier ----------
  const leagueChips =
    (LEAGUE_RULESETS[leagueType] && LEAGUE_RULESETS[leagueType].chips) ||
    (LEAGUE_RULESETS.default && LEAGUE_RULESETS.default.chips) ||
    {};

  let totalPoints = 0;
  const breakdowns = {};

  for (const playerId of selectedPlayers) {
    const playerScore = playerPoints[playerId] || { total: 0, breakdown: {} };
    const base = Number(playerScore.total || 0);

    let multiplier = 1;
    if (captain && playerId === captain) {
      const capMult = leagueChips?.[chipUsed]?.captainMultiplier;
      multiplier = capMult != null ? capMult : 2; // default x2 if captain and no chip
    }

    const final = base * multiplier;

    breakdowns[playerId] = {
      base,
      multiplier,
      final,
      details: playerScore.breakdown || {},
    };

    totalPoints += final;
  }

  // ---------- transfer penalty ----------
  // New logic: if currentGW === team.startGW, no penalty (unlimited transfers)
  // Otherwise, penalty = (transfersThisGW - freeTransfers) * penaltyPerTransfer if transfers exceed free transfers

  const maxFreeTransfers =
    typeof team.maxFreeTransfers === "number"
      ? team.maxFreeTransfers
      : typeof leagueConfig.maxFreeTransfers === "number"
      ? leagueConfig.maxFreeTransfers
      : 1;

  const penaltyPerTransfer =
    typeof leagueConfig.transferPenaltyPoints === "number"
      ? leagueConfig.transferPenaltyPoints
      : 5;

  // transfersMade and transfersFree can be saved under different key styles; try them all
  console.log("🔎 transfersMade and transfersFree field snapshot", {
    transfersMadeRaw: team.transfersMade,
    transfersFreeRaw: team.transfersFree,
    gwKey,
    currentGW,
    transfersMadeKeys: team.transfersMade ? Object.keys(team.transfersMade) : [],
    transfersFreeKeys: team.transfersFree ? Object.keys(team.transfersFree) : [],
  });

  let transferCount = (
    (team.transfersMade?.[gwKey] ??
      team.transfersMade?.[String(currentGW)] ??
      team.transfersMade?.[currentGW]) ??
    0
  );

  let freeTransfers = (
    (team.transfersFree?.[gwKey] ??
      team.transfersFree?.[String(currentGW)] ??
      team.transfersFree?.[currentGW]) ??
    maxFreeTransfers
  );

  let transferPenalty = 0;

  if (currentGW === team.startGW) {
    transferPenalty = 0;
    console.log(
      `Transfer Penalty (gw=${gwKey}): currentGW equals startGW (${currentGW}), no penalty applied.`
    );
  } else if (typeof transferCount === "number" && transferCount > freeTransfers) {
    const extra = transferCount - freeTransfers;
    transferPenalty = extra * penaltyPerTransfer;
    totalPoints -= transferPenalty;
    console.log(
      `Transfer Penalty (gw=${gwKey}) -> transfersMade=${transferCount}, freeTransfers=${freeTransfers}, per=${penaltyPerTransfer}, penalty=${transferPenalty}`
    );
  } else {
    console.log(
      `Transfer Penalty (gw=${gwKey}) -> transfersMade=${transferCount}, freeTransfers=${freeTransfers}, per=${penaltyPerTransfer}, penalty=0`
    );
  }

  return {
    total: totalPoints,
    breakdowns,
    selections,
    captain,
    chipUsed: chipUsed || null,
    transferPenalty,
  };
}